import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import { changePassword, reloadSnapshot } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

// ---------------- Security editor ----------------
export default function SecurityEditor() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(false);
    if (next.length < 6) { setErr("La nuova password deve avere almeno 6 caratteri"); return; }
    if (next !== confirm) { setErr("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      setOk(true);
      setCurrent(""); setNext(""); setConfirm("");
      toast({ title: "Password aggiornata" });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg">
      <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Cambia password admin</h2>
      <p className="mt-1 text-sm text-[#7c5a4e]">Aggiorna la password del pannello amministratore. La nuova password viene salvata in modo sicuro sul database.</p>

      <form onSubmit={submit} className="mt-5 bg-white border border-[#e8dcc8] rounded-xl p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Password attuale</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Nuova password</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20" />
          <p className="mt-1 text-xs text-[#7c5a4e]">Almeno 6 caratteri.</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Conferma nuova password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20" />
        </div>

        {err && <p className="text-sm text-red-700">{err}</p>}
        {ok && <p className="text-sm text-emerald-700">Password aggiornata correttamente.</p>}

        <div className="flex justify-end">
          <button type="submit" disabled={loading} className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-medium disabled:opacity-60">
            <KeyRound className="h-4 w-4" /> {loading ? "Salvataggio..." : "Aggiorna password"}
          </button>
        </div>
      </form>

      <SnapshotReloader />
    </div>
  );
}

// ---------------- Snapshot reloader (recovery tool) ----------------
function SnapshotReloader() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Attenzione: questa operazione CANCELLA tutti i menu, categorie, piatti e recensioni attuali e li ricarica dal file data_snapshot.json (versione preview). Procedere?"
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await reloadSnapshot();
      setResult(r);
      toast({ title: "Snapshot ricaricato", description: `${r.counts.dishes} piatti, ${r.counts.menus} menu, ${r.counts.reviews} recensioni` });
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border border-amber-300 bg-amber-50 rounded-xl p-6" data-testid="snapshot-reloader">
      <h3 className="font-serif text-xl font-bold text-[#2b1410]">Ricarica dati da snapshot</h3>
      <p className="mt-1 text-sm text-[#7c5a4e]">
        Usa questo tasto solo se la produzione mostra il menu demo in inglese al posto di quello reale.
        Cancella tutti i dati esistenti e ricarica dal file <code>data_snapshot.json</code> contenente
        i tuoi 12 menu, 90 categorie, 367 piatti, 100 recensioni e la copertina.
      </p>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="snapshot-reload-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 text-sm font-medium disabled:opacity-60"
      >
        {busy ? "Ricaricamento in corso…" : "Ricarica dati reali"}
      </button>
      {result && (
        <p className="mt-3 text-xs text-emerald-800">
          Ricaricati: {result.counts.menus} menu · {result.counts.categories} categorie · {result.counts.dishes} piatti · {result.counts.reviews} recensioni · copertina.
        </p>
      )}
    </div>
  );
}
